import {CardEnum, type KnightPlayerState, type KnightState, type NestedReadonly, type PlayerColor, TileCornerBuildingType} from '@colonist/game-states'
import {type PlayerValidator} from '../core/PlayerValidator.ts'
import {type ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {getCardsForPiece} from '@colonist/game-card-types'
import {type MapValidator} from '../core/map/MapValidator.ts'

export class MechanicValidatorKnightPlayer {
    private readonly playerValidator: PlayerValidator
    private readonly mapValidator: MapValidator
    private readonly knightPlayerState: NestedReadonly<KnightPlayerState>
    readonly playerColor: PlayerColor

    constructor(playerValidator: PlayerValidator, mapValidator: MapValidator, knightPlayerState: NestedReadonly<KnightPlayerState>) {
        this.playerValidator = playerValidator
        this.mapValidator = mapValidator
        this.knightPlayerState = knightPlayerState
        this.playerColor = this.playerValidator.color
    }

    getKnightPlayerState(): NestedReadonly<KnightPlayerState> {
        return this.knightPlayerState
    }

    getKnightStates(): NestedReadonly<KnightState[]> {
        const knightStates = Object.values(this.knightPlayerState.knights)
        return knightStates.filter((knight) => knight !== undefined) as NestedReadonly<KnightState[]>
    }

    getKnightAtLocation(cornerIndex: number): NestedReadonly<KnightState> | undefined {
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex !== cornerIndex) continue

            return knight
        }

        return undefined
    }

    getPlayerKnightStrength(): number {
        const knightStrengths = this.getKnightStates().map((knight) => MechanicValidatorKnightPlayer.getKnightStrength(knight))
        return knightStrengths.reduce((x, acc) => x + acc)
    }

    getKnightWithLevelFromInventory(level: number, ignoreIsLevelUnlocked: boolean = false): NestedReadonly<KnightState> | undefined {
        for(const knight of this.getKnightStates()) {
            const isKnightInInventory = knight.locationCornerIndex === undefined
            if(!isKnightInInventory) continue

            if(knight.level !== level) continue

            const isKnightLevelValid = ignoreIsLevelUnlocked || knight.level <= this.knightPlayerState.maxPurchaseableLevelUnlocked
            if(!isKnightLevelValid) continue

            return knight
        }

        return undefined
    }

    getKnightWithHighestLevelFromInventory(maximumLevel: number, ignoreUnlockRestriction: boolean): NestedReadonly<KnightState> | undefined {
        for(let currentLevel = maximumLevel; currentLevel > 0; currentLevel--) {
            const knightWithLevel = this.getKnightWithLevelFromInventory(currentLevel, ignoreUnlockRestriction)
            if(knightWithLevel === undefined) continue

            return knightWithLevel
        }

        return undefined
    }

    getKnightsWithLevelFromMap(level: number): NestedReadonly<KnightState>[] {
        const allKnightsWithLevel: NestedReadonly<KnightState>[] = []
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex === undefined) continue
            if(knight.level !== level) continue
            allKnightsWithLevel.push(knight)
        }

        return allKnightsWithLevel
    }

    getAmountOfKnightsEligibleForActivation(): number {
        let knightsEligibleForActivation: number = 0
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex === undefined) continue
            if(knight.isActivated === true) continue
            if(knight.isActivatedThisTurn) continue

            knightsEligibleForActivation += 1
        }

        return knightsEligibleForActivation
    }

    getAmountOfKnightsEligibleForUpgrade(): number {
        let knightsEligibleForUpgrade: number = 0
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex === undefined) continue
            if(knight.isUpgradedThisTurn) continue

            const higherLevelKnightAvailable = this.getKnightWithLevelFromInventory(knight.level + 1)
            if(higherLevelKnightAvailable == undefined) continue

            knightsEligibleForUpgrade += 1
        }
        return knightsEligibleForUpgrade
    }

    getAmountOfKnightsOfLevelThatCanBePlacedOnMap(level: number): number {
        let amountOfKnightsOfLevelThatCanBePlacedOnMap: number = 0
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex !== undefined) continue
            if(knight.level !== level) continue

            amountOfKnightsOfLevelThatCanBePlacedOnMap += 1
        }
        return amountOfKnightsOfLevelThatCanBePlacedOnMap
    }

    canPlayerPlaceNewKnight(useResources: boolean): ActionStatus {
        const where = this.whereCanPlayerPlaceNewKnight()
        const doesPlayerHaveResourcesAndPiecesForNewKnight = this.hasResourcesAndPiecesForNewKnight(useResources)
        doesPlayerHaveResourcesAndPiecesForNewKnight.validHoverLocations = where
        if(!doesPlayerHaveResourcesAndPiecesForNewKnight.canTakeAction) return doesPlayerHaveResourcesAndPiecesForNewKnight

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noWhereToPlaceKnight'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerPlaceNewKnight(): number[] {
        const placesToBuildNewKnights: number[] = []
        for(const tileEdge of this.mapValidator.tileState.tileEdges) {
            if(tileEdge.type == undefined) continue
            if(tileEdge.owner != this.playerColor) continue
            for(const touchingCorners of tileEdge.hexEdge.endPoints()) {
                const touchingTileCorner = this.mapValidator.tileState.getTileCorner(touchingCorners)
                if(touchingTileCorner == undefined) continue
                if(touchingTileCorner.buildingType != undefined) continue
                if(touchingTileCorner.owner != undefined) continue
                if(!this.mapValidator.tileState.isCornerTouchingLand(touchingTileCorner)) continue

                const cornerIndex = this.mapValidator.tileState.getTileCornerIndex(touchingTileCorner.hexCorner)
                if(cornerIndex == undefined) continue

                placesToBuildNewKnights.push(cornerIndex)
            }
        }
        return placesToBuildNewKnights
    }

    hasResourcesAndPiecesForNewKnight(useResources: boolean): ActionStatus {
        const unusedLevel1Knight = this.getKnightWithLevelFromInventory(1)
        if(unusedLevel1Knight == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughLevelOneKnights'}}
        if(!this.hasResourcesForKnight(useResources).canTakeAction) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    hasResourcesForKnight(useResources: boolean): ActionStatus {
        if(!useResources) return {canTakeAction: true}

        const resourcesForKnight = getCardsForPiece(PieceTypes.Knight)
        const hasResourcesForKnight = this.playerValidator.hasResourceCardsInHand(resourcesForKnight)
        if(!hasResourcesForKnight) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerUpgradeKnight(): ActionStatus {
        const useResources = true
        const where = this.whereCanPlayerUpgradeKnight()
        const doesPlayerHaveResourcesForKnightUpgrade = this.hasResourcesForKnightUpgrade(useResources)
        doesPlayerHaveResourcesForKnightUpgrade.validHoverLocations = where
        if(!doesPlayerHaveResourcesForKnightUpgrade.canTakeAction) return doesPlayerHaveResourcesForKnightUpgrade

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noKnightsToUpgrade'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerUpgradeKnight(): number[] {
        const whereCanPlayerUpgradeKnight: number[] = []
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex == undefined) continue
            if(knight.isUpgradedThisTurn) continue

            const knightWithHigherLevel = this.getKnightWithLevelFromInventory(knight.level + 1)
            const doesPlayerHaveAccessToKnightOfHigherLevel = knightWithHigherLevel !== undefined
            if(!doesPlayerHaveAccessToKnightOfHigherLevel) continue

            whereCanPlayerUpgradeKnight.push(knight.locationCornerIndex)
        }

        return whereCanPlayerUpgradeKnight
    }

    hasResourcesForKnightUpgrade(useResources: boolean): ActionStatus {
        const costToPurchaseKnight = useResources ? getCardsForPiece(PieceTypes.Knight) : []
        const doesPlayerHaveResourcesToUpgradeKnight = this.playerValidator.hasResourceCardsInHand(costToPurchaseKnight)
        if(!doesPlayerHaveResourcesToUpgradeKnight) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerActivateKnight(): ActionStatus {
        const where = this.whereCanPlayerActivateKnight()
        const doesPlayerHaveResourcesForKnightActivation = this.hasResourcesForKnightActivation()
        doesPlayerHaveResourcesForKnightActivation.validHoverLocations = where
        if(!doesPlayerHaveResourcesForKnightActivation.canTakeAction) return doesPlayerHaveResourcesForKnightActivation

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noKnightsToActivate'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerActivateKnight(): number[] {
        const whereCanPlayerActivateKnight: number[] = []
        for(const knight of this.getKnightStates()) {
            if(knight.locationCornerIndex == undefined) continue
            if(knight.isActivated === true) continue
            if(knight.isActivatedThisTurn === true) continue

            whereCanPlayerActivateKnight.push(knight.locationCornerIndex)
        }

        return whereCanPlayerActivateKnight
    }

    hasResourcesForKnightActivation(): ActionStatus {
        const costToActivateKnight = [CardEnum.Grain]
        const doesPlayerHaveResources = this.playerValidator.hasResourceCardsInHand(costToActivateKnight)
        if(!doesPlayerHaveResources) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    static getKnightStrength(knight: KnightState): number {
        if(knight.locationCornerIndex === undefined) return 0
        if(knight.isActivated === false) return 0

        return knight.level
    }

    static getKnightCornerBuildingType(knight: KnightState): TileCornerBuildingType | undefined {
        switch(knight.isActivated + '|' + knight.level) {
            case true + '|' + 1: return TileCornerBuildingType.KnightActiveLevel1
            case true + '|' + 2: return TileCornerBuildingType.KnightActiveLevel2
            case true + '|' + 3: return TileCornerBuildingType.KnightActiveLevel3
            case false + '|' + 1: return TileCornerBuildingType.KnightInactiveLevel1
            case false + '|' + 2: return TileCornerBuildingType.KnightInactiveLevel2
            case false + '|' + 3: return TileCornerBuildingType.KnightInactiveLevel3
        }
        return undefined
    }
}
